// Tiny in-memory customer cache shared across pages.
//
// The mini-CRM popup's document-level click delegation (installed once by
// the shell) needs a synchronous way to look up a customer's full record
// when the user clicks on a customer name anywhere in the app. Pages
// populate this cache as they load their own customer data — CRM,
// Contacts, Submissions etc. — and the cache survives navigation because
// it lives at the shell level.
//
// Indexed by both id and lowercased name so callers don't need to know
// which one the click site has.
//
// Falls back to "not found" if a customer name hasn't been seen yet —
// that's an acceptable UX (the popup shows the not-found state with an
// "Open CRM" affordance). The cache warms naturally as the user
// navigates through CRM-adjacent pages.

import type { MiniCrmCustomer } from "@tkos/shared-ui";

type Subscriber = () => void;

const byId   = new Map<string, MiniCrmCustomer>();
const byName = new Map<string, MiniCrmCustomer>();
const subs   = new Set<Subscriber>();

function notify(): void {
  for (const fn of subs) {
    try {
      fn();
    } catch (err) {
      // Never let one bad subscriber kill the others.
      console.error("[customer-cache] subscriber threw:", err);
    }
  }
}

function indexOne(c: MiniCrmCustomer): void {
  const id = String(c.id ?? "").trim();
  const name = String(c.name ?? "").trim().toLowerCase();
  if (id) byId.set(id, c);
  if (name) byName.set(name, c);
}

export const customerCache = {
  /** Merge a batch of customers into the cache. Overwrites by id+name. */
  merge(list: ReadonlyArray<MiniCrmCustomer>): void {
    if (list.length === 0) return;
    for (const c of list) indexOne(c);
    notify();
  },

  /** Look up by exact id OR by exact (case-insensitive) name. */
  get(idOrName: string): MiniCrmCustomer | null {
    const key = String(idOrName ?? "").trim();
    if (!key) return null;
    const hit = byId.get(key);
    if (hit) return hit;
    return byName.get(key.toLowerCase()) ?? null;
  },

  /** Sync resolver used by `installMiniCrmDelegation({ lookup })`. Prefers
   *  the supplied `focusId`, falls back to exact-name, then to a name
   *  prefix match (parity with source's `findCustomer`, line 17602). */
  lookup(name: string, focusId?: string): MiniCrmCustomer | null {
    if (focusId) {
      const hit = byId.get(focusId);
      if (hit) return hit;
    }
    const lc = String(name ?? "").trim().toLowerCase();
    if (!lc) return null;
    const exact = byName.get(lc);
    if (exact) return exact;
    for (const c of byName.values()) {
      if (c.name && c.name.toLowerCase().startsWith(lc)) return c;
    }
    return null;
  },

  /** Evict a single customer by id (e.g. after a delete) so a stale
   *  mini-CRM popup can't resurrect it. Removes the `byId` entry, and the
   *  `byName` entry only when it still maps to this same id — so a contact
   *  that shares a name with another isn't evicted by mistake. */
  remove(id: string): void {
    const key = String(id ?? "").trim();
    if (!key) return;
    const existing = byId.get(key);
    const had = byId.delete(key);
    if (existing) {
      const name = String(existing.name ?? "").trim().toLowerCase();
      if (name) {
        const byNameHit = byName.get(name);
        if (byNameHit && String(byNameHit.id ?? "").trim() === key) {
          byName.delete(name);
        }
      }
    }
    if (had) notify();
  },

  /** Snapshot of every cached customer — used by the CRM page when it
   *  needs to scroll to a customer that's already in cache. */
  all(): MiniCrmCustomer[] {
    return Array.from(byId.values());
  },

  /** Subscribe to cache changes. Returns a teardown function. */
  subscribe(fn: Subscriber): () => void {
    subs.add(fn);
    return () => subs.delete(fn);
  },

  /** Test/HMR only — clears the cache. */
  _reset(): void {
    byId.clear();
    byName.clear();
    notify();
  },
};
